// Lightweight inline SVG icon set — keeps bundle small and crisp
import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

const baseProps = (size: number) => ({
  width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
  strokeWidth: 1.75, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
});

export const Icon = {
  Search: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
  ),
  Heart: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z"/></svg>
  ),
  Star: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
  ),
  User: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
  ),
  Menu: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="18" y2="18"/></svg>
  ),
  X: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
  ),
  MapPin: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M20 10c0 7-8 12-8 12s-8-5-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
  ),
  Calendar: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><rect width="18" height="18" x="3" y="4" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
  ),
  Users: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
  ),
  Plane: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2Z"/></svg>
  ),
  Bed: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M2 4v16M22 4v16M2 8h20M2 16h20M6 12h4"/></svg>
  ),
  Car: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2"/><circle cx="6.5" cy="16.5" r="2.5"/><circle cx="16.5" cy="16.5" r="2.5"/></svg>
  ),
  Compass: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>
  ),
  Sparkles: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/><path d="M20 3v4M22 5h-4M4 17v2M5 18H3"/></svg>
  ),
  Globe: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20M2 12h20"/></svg>
  ),
  Chat: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
  ),
  Send: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></svg>
  ),
  ChevronRight: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="m9 18 6-6-6-6"/></svg>
  ),
  ChevronLeft: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="m15 18-6-6 6-6"/></svg>
  ),
  ChevronDown: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="m6 9 6 6 6-6"/></svg>
  ),
  Check: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M20 6 9 17l-5-5"/></svg>
  ),
  CreditCard: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/></svg>
  ),
  CheckCircle: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/></svg>
  ),
  Clock: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
  ),
  Filter: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
  ),
  Home: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
  ),
  Shield: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
  ),
  Award: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/></svg>
  ),
  TrendingUp: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
  ),
  TrendingDown: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><polyline points="22 17 13.5 8.5 8.5 13.5 2 7"/><polyline points="16 17 22 17 22 11"/></svg>
  ),
  LogOut: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" x2="9" y1="12" y2="12"/></svg>
  ),
  Plus: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M5 12h14M12 5v14"/></svg>
  ),
  Trash: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
  ),
  Edit: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
  ),
  Image: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
  ),
  Phone: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
  ),
  Mail: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-10 5L2 7"/></svg>
  ),
  Settings: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
  ),
  Bell: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>
  ),
  Eye: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
  ),
  EyeOff: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><line x1="2" x2="22" y1="2" y2="22"/></svg>
  ),
  Currency: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><line x1="12" x2="12" y1="2" y2="22"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
  ),
  Building: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4M8 6h.01M16 6h.01M12 6h.01M8 10h.01M16 10h.01M12 10h.01M8 14h.01M16 14h.01M12 14h.01"/></svg>
  ),
  Tag: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"/><circle cx="7.5" cy="7.5" r=".5"/></svg>
  ),
  Layers: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>
  ),
  Activity: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
  ),
  Briefcase: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><rect width="20" height="14" x="2" y="7" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>
  ),
  LifeBuoy: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/><path d="m4.93 4.93 4.24 4.24M14.83 14.83l4.24 4.24M14.83 9.17l4.24-4.24M14.83 9.17l3.53-3.53M4.93 19.07l4.24-4.24"/></svg>
  ),
  HelpCircle: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
  ),
  FileText: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="16" x2="8" y1="13" y2="13"/><line x1="16" x2="8" y1="17" y2="17"/><line x1="10" x2="8" y1="9" y2="9"/></svg>
  ),
  BarChart: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><line x1="12" x2="12" y1="20" y2="10"/><line x1="18" x2="18" y1="20" y2="4"/><line x1="6" x2="6" y1="20" y2="16"/></svg>
  ),
  ShoppingBag: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" x2="21" y1="6" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
  ),
  Sparkle: ({ size = 20, ...p }: IconProps) => (
    <svg {...baseProps(size)} {...p}><path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3z"/></svg>
  ),
};
